import React, { forwardRef, memo, ReactElement, Ref } from 'react'

import { LoadingOutlined } from '@ant-design/icons'
import { Divider, Input, type InputProps } from 'antd'
import Downshift, {
    ControllerStateAndHelpers,
    DownshiftState,
    StateChangeOptions,
} from 'downshift'
import { isNil } from 'lodash'
import xorBy from 'lodash/xorBy'
import { useTranslation } from 'react-i18next'

import { ColoredTag } from 'components/ColoredTag'
import { Icon as CoreIcon } from 'components/core'
import { SlashCircleIcon } from 'components/Icons'
import { IconProps } from 'components/Icons/localTypes'
import { TypeaheadOption, TypeaheadPagination } from 'types'

import * as styles from './styles.scss'
import TypeaheadMenu from './TypeaheadMenu'
import { TypeaheadMenuItemProps, TypeaheadMenuProps } from './types'

interface Props<ItemType extends TypeaheadOption = TypeaheadOption> {
    loading?: boolean
    options?: ItemType[]
    values: ItemType[]
    color?: string
    placeholder: string
    disableEmptyState?: boolean
    pagination?: TypeaheadPagination

    renderMenu?: (props: TypeaheadMenuProps<ItemType>) => ReactElement
    renderItem?: (props: TypeaheadMenuItemProps<ItemType>) => ReactElement
    renderTagContent?: (props: ItemType) => string | number | ReactElement
    onChangeInput(inputValue: string): void
    onChange: (values: ItemType[]) => void
}

const downshiftStateReducer = <
    ItemType extends TypeaheadOption = TypeaheadOption,
>(
    state: DownshiftState<ItemType>,
    changes: StateChangeOptions<ItemType>
): Partial<StateChangeOptions<ItemType>> => {
    switch (changes.type) {
        case Downshift.stateChangeTypes.keyDownEnter:
        case Downshift.stateChangeTypes.mouseUp:
        case Downshift.stateChangeTypes.clickItem:
            return {
                ...changes,
                isOpen: state.isOpen,
                highlightedIndex: state.highlightedIndex,
                inputValue: state.inputValue,
            }
        default:
            return changes
    }
}

const defaultRenderMenu = <ItemType extends TypeaheadOption = TypeaheadOption>(
    props: TypeaheadMenuProps<ItemType>
): ReactElement => <TypeaheadMenu {...props} />

const defaultRenderTagContent = (
    option: TypeaheadOption
): string | number | ReactElement => option.label

const Typeahead = <ItemType extends TypeaheadOption = TypeaheadOption>(
    {
        loading = false,
        options = [],
        values,
        color,
        placeholder,
        disableEmptyState = false,
        pagination,
        renderMenu = defaultRenderMenu,
        renderItem,
        renderTagContent = defaultRenderTagContent,
        onChangeInput,
        onChange,
    }: Props<ItemType>,
    ref?: Ref<HTMLInputElement>
): ReactElement => {
    const { t } = useTranslation('common')

    const handleToggleOption = (option: ItemType): void => {
        onChange(xorBy(values, [option], 'value'))
    }

    const syncDownshiftState = ({
        type,
        selectedItem,
        inputValue,
    }: StateChangeOptions<ItemType>): void => {
        if (type === Downshift.stateChangeTypes.keyDownEnter && selectedItem) {
            handleToggleOption(selectedItem)
        }

        if (
            type === Downshift.stateChangeTypes.changeInput &&
            !isNil(inputValue)
        ) {
            onChangeInput(inputValue)
        }
    }

    const emptyState = t(
        'common:typeahead.emptyState',
        'Start typing to search...'
    )
    const noResult = t('common:typeahead.noResults', 'No results found')

    return (
        <Downshift
            defaultIsOpen
            selectedItem={null}
            itemToString={(item) => (item ? item.label : '')}
            stateReducer={downshiftStateReducer}
            onStateChange={syncDownshiftState}
        >
            {(downshift: ControllerStateAndHelpers<ItemType>) => {
                const renderBlankState = (
                    Icon: React.ComponentType<IconProps>,
                    message: string
                ): ReactElement => (
                    <div className={styles['blank-state']}>
                        <Icon className={styles['blank-state-icon']} />
                        <span>{message}</span>
                    </div>
                )

                const displayMenu = renderMenu({
                    downshift,
                    options,
                    values,
                    onToggleOption: handleToggleOption,
                    renderItem,
                    pagination,
                })

                let content: ReactElement | null = null

                if (!disableEmptyState && loading) {
                    content = null
                } else if (disableEmptyState) {
                    content = displayMenu
                    if (
                        downshift.inputValue &&
                        options.length === 0 &&
                        !loading
                    ) {
                        content = (
                            <>
                                {displayMenu}
                                {renderBlankState(SlashCircleIcon, noResult)}
                            </>
                        )
                    }
                } else if (options.length > 0) {
                    content = displayMenu
                } else if (downshift.inputValue?.trim()) {
                    content = renderBlankState(SlashCircleIcon, noResult)
                } else {
                    content = renderBlankState(
                        () => (
                            <CoreIcon
                                name="SEARCH_NAV"
                                color="textExtraLight"
                                size={17}
                            />
                        ),
                        emptyState
                    )
                }

                return (
                    <div className={styles.typeahead}>
                        <div style={{ margin: '-10px 0 0', height: '40px' }}>
                            <Input
                                {...(downshift.getInputProps({
                                    ref,
                                    placeholder,
                                    suffix: loading ? (
                                        <LoadingOutlined />
                                    ) : (
                                        <span />
                                    ),
                                }) as unknown as InputProps)}
                                variant="borderless"
                            />

                            <Divider
                                className={styles['selected-options-divider']}
                            />
                        </div>

                        {content}

                        {values.length > 0 && (
                            <div className={styles['selected-options']}>
                                <Divider
                                    className={
                                        styles['selected-options-divider']
                                    }
                                />
                                {values.map((option) => (
                                    <ColoredTag
                                        key={option?.value?.toString()}
                                        className={styles.tag}
                                        color={color}
                                        closable
                                        onClose={() =>
                                            handleToggleOption(option)
                                        }
                                    >
                                        {renderTagContent(option)}
                                    </ColoredTag>
                                ))}
                            </div>
                        )}
                    </div>
                )
            }}
        </Downshift>
    )
}

export default memo(forwardRef<HTMLInputElement, Props>(Typeahead)) as <
    T extends TypeaheadOption = TypeaheadOption,
>(
    props: Props<T> & { ref?: Ref<HTMLInputElement> }
) => ReturnType<typeof Typeahead<T>>
